import {HttpErrorResponse} from '@angular/common/http';
import {AfterViewInit, Component, ElementRef, Inject, LOCALE_ID, OnDestroy, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable} from 'rxjs';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {SubmitRowButtonsComponent} from '~shared/components/buttons/submit-row-buttons/submit-row-buttons.component';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {AuthenticatedUser, Club, Country} from '~core/models';
import {ClubService, CountryService, DefaultNotificationService, NavigationService, StorageService,} from '~core/services';
import {ClubListTranslationsService, ClubTranslationsService, TooltipTranslationService} from '~shared/translations';

/**
 * Component responsible for club-overview administration.
 */
@UntilDestroy()
@Component({
  selector: 'app-club-overview-list',
  templateUrl: './club-list.component.html'
})
export class ClubListComponent implements AfterViewInit, OnDestroy {
  @ViewChild('loadMore') loadMore: ElementRef;
  public loading: boolean = true;
  public listOfClubs: Array<Club>;
  public authenticatedUser?: AuthenticatedUser;
  public modalOpened: boolean = false;
  public spinnerName: string = 'clubListSubmit';
  public loadMoreSpinner: string = 'clubListLoad';
  public clubLoadSpinner: string = 'clubListLoading';
  public loadMoreLoading: boolean = false;
  public clubLoadLoading: boolean = false;
  public maxClubs: boolean = false;
  public clubForm: UntypedFormGroup;
  public searchForm: UntypedFormGroup;
  public countries: Array<Country> = [];
  public currentPage: number = 1;
  public previousInput: string = '';

  public searchByClubName: string = $localize`:@@clubListComponent.placeholder.searchByClubName:Search by club name`;

  @ViewChildren(SubmitRowButtonsComponent) public srButtons: QueryList<SubmitRowButtonsComponent>;

  constructor(
    public clubService: ClubService,
    private notificationService: DefaultNotificationService,
    private router: Router,
    private clubListTranslations: ClubListTranslationsService,
    private storage: StorageService,
    public tooltipTranslations: TooltipTranslationService,
    public flTranslations: FormLabelTranslationService,
    public spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations,
    public countryService: CountryService,
    public formBuilder: UntypedFormBuilder,
    public navigationService: NavigationService,
    @Inject(LOCALE_ID) public locale: string,
    private clubTranslations: ClubTranslationsService
  ) {
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe((countries: Array<Country>) => this.countries = countries);
    this.storage.castAuthenticatedUser.pipe(untilDestroyed(this))
      .subscribe((user: AuthenticatedUser) => user !== null ? this.authenticatedUser = user : null);
    this.clubService.castListOfClubs.pipe(untilDestroyed(this))
      .subscribe((clubs: Array<Club>) => this.listOfClubs = clubs);
    this.navigationService.setCurrentBreadcrumbs(['globalSettings', 'globalClubList']);
  }

  public ngOnDestroy(): void {
  }

  /**
   * On init create the club-overview form, get countries and all clubs.
   */
  public ngAfterViewInit(): any {
    this.retrieveClubs();
  }

  /**
   * Executes the search query. If only result contains only 1 result, it will
   * automatically redirect you to that club-overview.
   *
   * @param event Emitted event.
   * @param byEnter If its triggered by enter key.
   */
  public getCustomerFromSearch(event: any, byEnter: boolean): any {
    this.currentPage = 1;
    this.searchForm.markAsTouched();
    if (this.searchForm.valid && this.searchForm.value['search'].length) {
      this.spinnerService.show(this.clubLoadSpinner);
      const query = event.target.value;

      if (this.previousInput !== query) {
        this.previousInput = query;

        this.clubService.searchClubs(this.authenticatedUser, query, this.currentPage).pipe(untilDestroyed(this))
          .subscribe(data => {
            this.currentPage = 1;
            this.checkMax(data.clubs.length, data.totalItems);
            this.spinnerService.hide(this.clubLoadSpinner);
            if (data.length !== 0) {
              if (data.clubs.length === 1 && byEnter) {
                this.selectClub(data.clubs[0]);
              }
              this.clubService.setListOfClubs(data.clubs);
              this.getLogos();
            } else {
              this.clubService.setListOfClubs(null);
              this.notificationService.showNotification({
                type: NotificationType.WARNING,
                message: this.clubListTranslations.i18nErrorFetchingClubs
              });
            }
            this.spinnerService.hide(this.clubLoadSpinner);
          });
      } else {
        if (this.listOfClubs && this.listOfClubs.length === 1 && byEnter) {
          this.selectClub(this.listOfClubs[0]);
        }
        this.spinnerService.hide(this.clubLoadSpinner);
      }
    } else if (this.searchForm.valid && !this.searchForm.value['search']) {
      this.retrieveClubs();
    }
  }

  /**
   * Select the club-overview
   */
  public selectClub(club: Club): void {
    this.storage.updateSelectedClub(club);
    this.router.navigate([`/club/${club.id}`]);
  }

  /**
   * On form submit create a new club-overview.
   */
  public createNewClub(): any {
    if (this.clubForm.valid) {
      this.spinnerService.show(this.spinnerName);
      this.clubService.createNewClub(this.clubForm.value).pipe(untilDestroyed(this))
        .subscribe({
          next: (res: Club) => {
            this.spinnerService.hide(this.spinnerName);
            this.resetForm();
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.clubListTranslations.i18nSuccessClubCreate
            });
            this.currentPage = 1;
            this.retrieveClubs();
          },
          error: (error: HttpErrorResponse) => {
            this.spinnerService.hide(this.spinnerName);
            if (error.status === 400) {
              for (const violation of error.error.violations) {
                if (violation.propertyPath === 'name') {
                  this.notificationService.showNotification({
                    type: NotificationType.ALERT,
                    message: this.clubListTranslations.i18nClubNameExists
                  });
                  return;
                }
              }
            } else {
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: this.clubListTranslations.i18nErrorClubCreate
              });
            }
          }
        });
    }
  }

  /**
   * Set the modal status.
   */
  public setModalStatus(status: boolean): void {
    this.modalOpened = status;
  }

  /**
   * Fills the form with empty values
   */
  public fillForm(): void {
    this.clubForm = this.formBuilder.group({
      name: [null, Validators.required],
      country: ['DE', Validators.required],
    });

    this.searchForm = this.formBuilder.group({
      search: [null, [Validators.minLength(2)]]
    });
  }

  /**
   * Resets the form
   */
  public resetForm(): void {
    this.clubForm.reset();
    this.setModalStatus(false);
    this.fillForm();
    this.previousInput = '';
  }

  /**
   * Fetch path / name of the logos.
   */
  public getLogos(): void {
    for (const club of this.listOfClubs) {
      if (club.logo !== null && club.pathToLogo === undefined) {
        this.clubService.getPathToImageByMediaObject(club.logo)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (res: any) => {
              club.pathToLogo = res.contentUrl;
            },
            error: (err: Error) => {
              this.notificationService.showNotification({
                type: NotificationType.ALERT,
                message: this.clubTranslations.i18nErrorCannotFetchLogo
              });
            }
          });
      }
    }
  }

  /**
   * Retrieves the country name from country code
   */
  public getAddress(club: Club): any {
    const addressArr = [club.street, club.city, this.countryService.getCountry(club.country, this.countries)];
    return addressArr.filter(Boolean).join(', ');
  }

  /**
   * Load more clubs
   */
  public showMore(): void {
    this.currentPage++;
    this.spinnerService.show(this.loadMoreSpinner);
    let whichService: Observable<any>;
    if (this.searchForm.value['search'] && this.searchForm.value['search'].length) {
      whichService = this.clubService.searchClubs(this.authenticatedUser, this.previousInput, this.currentPage);
    } else {
      whichService = this.clubService.fetchAuthenticatedUserClubs(this.currentPage);
    }
    whichService.pipe(untilDestroyed(this)).subscribe((data: any) => {
        const addedClubs = this.listOfClubs.concat(data.clubs);
        this.clubService.setListOfClubs(addedClubs);
        this.checkMax(addedClubs.length, data.totalItems);
        this.spinnerService.hide(this.loadMoreSpinner);
        this.getLogos();
        setTimeout(() => {
          this.getScrollPosition();
        }, 100);
      },
      (error: Error) => {
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: this.clubListTranslations.i18nErrorFetchingClubs
        });
      });
  }

  /**
   * Checks if all entries are currently shown on screen
   */
  public checkMax(clubs: number, totalClubs: number): void {
    this.maxClubs = clubs === totalClubs;
  }

  /**
   * Retrieve all the necessary clubs
   */
  public retrieveClubs(): void {
    this.fillForm();
    this.spinnerService.show(this.clubLoadSpinner);
    this.clubService.fetchAuthenticatedUserClubs().pipe(untilDestroyed(this))
      .subscribe({
        next: (data: any) => {
          this.clubService.setListOfClubs(data.clubs);
          this.checkMax(data.clubs.length, data.totalItems);
          this.loading = false;
          this.spinnerService.hide(this.clubLoadSpinner);
          this.getLogos();
          setTimeout(() => {
            this.getScrollPosition();
          }, 100);
        },
        error: (error: Error) => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.clubListTranslations.i18nErrorFetchingClubs
          });
        }
      });
  }

  public getScrollPosition() {
    if (this.loadMore) {
      const rect = this.loadMore.nativeElement.getBoundingClientRect();
      const elemTop = rect.top;
      const elemBottom = rect.bottom;
      // Only completely visible elements return true:
      const isVisible = (elemTop >= 0) && (elemBottom <= window.innerHeight);
      // Partially visible elements return true:
      //isVisible = elemTop < window.innerHeight && elemBottom >= 0;
      if (isVisible) {
        this.showMore();
      }
    }
  }
}
